// Big hero sprite: tall senior cloud engineer in pixel art — short hair, glasses,
// hoodie with drawstrings and pocket, lanyard ID badge, coffee mug (idle/wave).
// SVG rect grid, animated via CSS groups: idle (breathe + blink + steam),
// typing (holo terminal + alternating hands), wave (greeting, mug in the other hand).
const C = {
  hair: "#0a1428",
  skin: "#e6b58d",
  skinShade: "#d19d74",
  frame: "#6ea8ff",
  lens: "#d6e6fb",
  hood: "#123fa6",
  hoodShade: "#0d2456",
  string: "#d6e6fb",
  lanyard: "#6ea8ff",
  card: "#eef4fd",
  pants: "#14294f",
  pantsShade: "#0d1e3d",
  shoe: "#0a1428",
  sole: "#d6e6fb",
  mug: "#d6e6fb",
  coffee: "#6b4226",
  holo: "#6ea8ff",
  holoDim: "#2e6fe0",
  gold: "#f2c14e",
  goldDeep: "#c9931a",
};

export type EngineerVariant =
  | "idle"
  | "typing"
  | "wave"
  | "climb"
  | "point"
  | "read"
  | "trophy"
  | "speak"
  | "cert"
  | "clipboard"
  | "laptop"
  | "walk";

export default function EngineerSprite({
  variant = "idle",
  className = "",
}: {
  variant?: EngineerVariant;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 30"
      className={`es es-${variant} ${className}`}
      style={{ imageRendering: "pixelated" }}
      shapeRendering="crispEdges"
      role="img"
      aria-label="Pixel art senior cloud engineer with hoodie, lanyard badge, and coffee"
    >
      <g className="es-body">
        {/* short hair */}
        <rect x="8" y="1" width="8" height="1" fill={C.hair} />
        <rect x="7" y="2" width="10" height="2" fill={C.hair} />
        {/* face */}
        <rect x="7" y="4" width="10" height="4" fill={C.skin} />
        <rect x="6" y="5" width="1" height="2" fill={C.skin} />
        <rect x="17" y="5" width="1" height="2" fill={C.skin} />
        {/* glasses */}
        <rect x="7" y="5" width="10" height="1" fill={C.frame} />
        <g className="es-eyes-open">
          <rect x="9" y="5" width="2" height="1" fill={C.lens} />
          <rect x="13" y="5" width="2" height="1" fill={C.lens} />
          <rect x="9" y="6" width="2" height="1" fill={C.hair} />
          <rect x="13" y="6" width="2" height="1" fill={C.hair} />
        </g>
        <g className="es-eyes-shut">
          <rect x="9" y="5" width="2" height="1" fill={C.lens} />
          <rect x="13" y="5" width="2" height="1" fill={C.lens} />
          <rect x="9" y="6" width="2" height="1" fill={C.skinShade} />
          <rect x="13" y="6" width="2" height="1" fill={C.skinShade} />
        </g>
        <rect x="11" y="7" width="3" height="1" fill={C.skinShade} />
        {/* neck */}
        <rect x="11" y="8" width="2" height="1" fill={C.skinShade} />

        {/* hoodie: collar ring, body, side shade */}
        <rect x="6" y="9" width="12" height="1" fill={C.hoodShade} />
        <rect x="7" y="10" width="10" height="7" fill={C.hood} />
        <rect x="7" y="10" width="1" height="7" fill={C.hoodShade} />
        {/* drawstrings */}
        <rect x="10" y="10" width="1" height="3" fill={C.string} />
        <rect x="13" y="10" width="1" height="3" fill={C.string} />
        {/* lanyard strands + ID badge */}
        <rect x="9" y="10" width="1" height="2" fill={C.lanyard} />
        <rect x="14" y="10" width="1" height="2" fill={C.lanyard} />
        <rect x="10" y="12" width="4" height="1" fill={C.lanyard} />
        <rect x="10" y="13" width="4" height="2" fill={C.card} />
        <rect x="10" y="13" width="1" height="2" fill={C.holoDim} />
        {/* kangaroo pocket */}
        <rect x="9" y="15" width="6" height="2" fill={C.hoodShade} />
        {/* hem */}
        <rect x="7" y="17" width="10" height="1" fill={C.hair} />
        {/* pants + shoes (base stance; climb swaps these for stride frames) */}
        <g className="es-legs-base">
          <rect x="8" y="18" width="3" height="9" fill={C.pants} />
          <rect x="13" y="18" width="3" height="9" fill={C.pants} />
          <rect x="8" y="18" width="1" height="9" fill={C.pantsShade} />
          <rect x="13" y="18" width="1" height="9" fill={C.pantsShade} />
          <rect x="7" y="27" width="4" height="2" fill={C.shoe} />
          <rect x="13" y="27" width="4" height="2" fill={C.shoe} />
          <rect x="7" y="29" width="4" height="1" fill={C.sole} />
          <rect x="13" y="29" width="4" height="1" fill={C.sole} />
        </g>
        {/* climb stride frames */}
        <g className="es-legs-climb-a">
          <rect x="7" y="19" width="4" height="2" fill={C.pants} />
          <rect x="6" y="21" width="2" height="3" fill={C.pants} />
          <rect x="5" y="24" width="3" height="2" fill={C.shoe} />
          <rect x="13" y="18" width="3" height="9" fill={C.pants} />
          <rect x="13" y="27" width="4" height="2" fill={C.shoe} />
          <rect x="13" y="29" width="4" height="1" fill={C.sole} />
        </g>
        <g className="es-legs-climb-b">
          <rect x="8" y="18" width="3" height="9" fill={C.pants} />
          <rect x="7" y="27" width="4" height="2" fill={C.shoe} />
          <rect x="7" y="29" width="4" height="1" fill={C.sole} />
          <rect x="13" y="19" width="4" height="2" fill={C.pants} />
          <rect x="16" y="21" width="2" height="3" fill={C.pants} />
          <rect x="16" y="24" width="3" height="2" fill={C.shoe} />
        </g>

        {/* arms: rest — left hand holds the mug */}
        <g className="es-arms-rest">
          <rect x="5" y="10" width="2" height="5" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="5" fill={C.hoodShade} />
          <rect x="5" y="15" width="2" height="2" fill={C.skin} />
          <rect x="17" y="15" width="2" height="2" fill={C.skin} />
          {/* mug in left hand */}
          <rect x="2" y="14" width="3" height="1" fill={C.coffee} />
          <rect x="2" y="15" width="3" height="2" fill={C.mug} />
          <rect x="1" y="15" width="1" height="1" fill={C.mug} />
          <g className="es-steam">
            <rect x="3" y="11" width="1" height="1" fill={C.string} />
            <rect x="2" y="12" width="1" height="1" fill={C.string} />
          </g>
        </g>

        {/* arms: typing frame A / B */}
        <g className="es-arms-type-a">
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="4" fill={C.hoodShade} />
          <rect x="4" y="13" width="3" height="2" fill={C.skin} />
          <rect x="17" y="14" width="3" height="2" fill={C.skin} />
        </g>
        <g className="es-arms-type-b">
          <rect x="5" y="10" width="2" height="4" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="4" y="14" width="3" height="2" fill={C.skin} />
          <rect x="17" y="13" width="3" height="2" fill={C.skin} />
        </g>

        {/* wave: left hand keeps the mug, right arm waves (A/B) */}
        <g className="es-arm-wave-a">
          <rect x="5" y="10" width="2" height="5" fill={C.hoodShade} />
          <rect x="5" y="15" width="2" height="2" fill={C.skin} />
          <rect x="2" y="14" width="3" height="1" fill={C.coffee} />
          <rect x="2" y="15" width="3" height="2" fill={C.mug} />
          <rect x="1" y="15" width="1" height="1" fill={C.mug} />
          <rect x="17" y="6" width="2" height="4" fill={C.hoodShade} />
          <rect x="17" y="4" width="2" height="2" fill={C.skin} />
        </g>
        <g className="es-arm-wave-b">
          <rect x="5" y="10" width="2" height="5" fill={C.hoodShade} />
          <rect x="5" y="15" width="2" height="2" fill={C.skin} />
          <rect x="2" y="14" width="3" height="1" fill={C.coffee} />
          <rect x="2" y="15" width="3" height="2" fill={C.mug} />
          <rect x="1" y="15" width="1" height="1" fill={C.mug} />
          <rect x="18" y="6" width="2" height="4" fill={C.hoodShade} />
          <rect x="19" y="4" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: climb — leading arm reaches up */}
        <g className="es-pose-climb">
          <rect x="5" y="6" width="2" height="4" fill={C.hoodShade} />
          <rect x="5" y="4" width="2" height="2" fill={C.skin} />
          <rect x="17" y="10" width="2" height="5" fill={C.hoodShade} />
          <rect x="17" y="15" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: point — arm extended to the work */}
        <g className="es-pose-point">
          <rect x="5" y="10" width="2" height="5" fill={C.hoodShade} />
          <rect x="5" y="15" width="2" height="2" fill={C.skin} />
          <rect x="17" y="11" width="4" height="2" fill={C.hoodShade} />
          <rect x="21" y="11" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: read — open book held at chest */}
        <g className="es-pose-read">
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="5" y="13" width="3" height="2" fill={C.skin} />
          <rect x="16" y="13" width="3" height="2" fill={C.skin} />
          <rect x="8" y="12" width="4" height="3" fill={C.card} />
          <rect x="12" y="12" width="4" height="3" fill={C.lens} />
          <rect x="11" y="12" width="2" height="3" fill={C.holoDim} />
        </g>

        {/* pose: trophy — big gold cup held in front: rim, handles, glint, stem, base */}
        <g className="es-pose-trophy">
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="8" y="10" width="8" height="1" fill={C.gold} />
          <rect x="9" y="11" width="6" height="3" fill={C.gold} />
          <rect x="10" y="11" width="1" height="2" fill={C.card} />
          <rect x="7" y="11" width="2" height="2" fill={C.goldDeep} />
          <rect x="15" y="11" width="2" height="2" fill={C.goldDeep} />
          <rect x="11" y="14" width="2" height="1" fill={C.goldDeep} />
          <rect x="9" y="15" width="6" height="1" fill={C.goldDeep} />
          <rect x="5" y="13" width="2" height="2" fill={C.skin} />
          <rect x="17" y="13" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: cert — big framed certificate held in front: white sheet on a blue
            frame, text lines, cyan seal with ribbon tails */}
        <g className="es-pose-cert">
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="6" y="10" width="12" height="7" fill={C.holoDim} />
          <rect x="7" y="11" width="10" height="5" fill={C.card} />
          <rect x="8" y="12" width="6" height="1" fill={C.holoDim} />
          <rect x="8" y="14" width="4" height="1" fill={C.frame} />
          <rect x="14" y="13" width="2" height="2" fill={C.frame} />
          <rect x="14" y="15" width="1" height="1" fill={C.hood} />
          <rect x="15" y="15" width="1" height="1" fill={C.hood} />
          <rect x="5" y="12" width="2" height="2" fill={C.skin} />
          <rect x="17" y="12" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: laptop — open laptop carried at the chest: dark lid with glowing
            code lines, wider base slab, both hands underneath */}
        <g className="es-pose-laptop">
          <rect x="8" y="8" width="8" height="5" fill={C.hair} />
          <rect x="9" y="9" width="6" height="3" fill={C.hoodShade} />
          <rect x="10" y="10" width="4" height="1" fill={C.frame} />
          <rect x="10" y="11" width="3" height="1" fill={C.holoDim} />
          <rect x="8" y="13" width="8" height="1" fill={C.holoDim} />
          <rect x="7" y="14" width="10" height="1" fill={C.hair} />
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="5" y="13" width="2" height="2" fill={C.skin} />
          <rect x="17" y="13" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: clipboard — reviewing the sheet: pale board with a clip, checklist
            lines with cyan ticks, both hands on the edges */}
        <g className="es-pose-clipboard">
          <rect x="11" y="8" width="2" height="1" fill={C.hair} />
          <rect x="10" y="9" width="4" height="1" fill={C.hair} />
          <rect x="8" y="10" width="8" height="7" fill={C.card} />
          <rect x="9" y="11" width="4" height="1" fill={C.holoDim} />
          <rect x="14" y="11" width="1" height="1" fill={C.frame} />
          <rect x="9" y="13" width="5" height="1" fill={C.holoDim} />
          <rect x="14" y="13" width="1" height="1" fill={C.frame} />
          <rect x="9" y="15" width="3" height="1" fill={C.holoDim} />
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="6" y="12" width="2" height="2" fill={C.skin} />
          <rect x="16" y="12" width="2" height="2" fill={C.skin} />
        </g>

        {/* pose: speak — mic held at the body's center with both hands: pale grille
            ball under the mouth, dark handle down the centerline, voice arcs */}
        <g className="es-pose-speak">
          {/* mic: ball, grille stripe, handle with band */}
          <rect x="10" y="6" width="4" height="4" fill={C.lens} />
          <rect x="10" y="7" width="4" height="1" fill={C.hair} />
          <rect x="11" y="10" width="2" height="4" fill={C.hair} />
          <rect x="11" y="11" width="2" height="1" fill={C.holoDim} />
          {/* both arms bent in, fists on the handle */}
          <rect x="5" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="7" y="12" width="2" height="1" fill={C.hoodShade} />
          <rect x="9" y="11" width="2" height="2" fill={C.skin} />
          <rect x="17" y="10" width="2" height="3" fill={C.hoodShade} />
          <rect x="15" y="12" width="2" height="1" fill={C.hoodShade} />
          <rect x="13" y="11" width="2" height="2" fill={C.skin} />
          {/* voice arcs both sides of the ball */}
          <rect x="8" y="4" width="1" height="1" fill={C.frame} />
          <rect x="6" y="3" width="1" height="1" fill={C.frame} />
          <rect x="15" y="4" width="1" height="1" fill={C.frame} />
          <rect x="17" y="3" width="1" height="1" fill={C.frame} />
        </g>
      </g>

      {/* holo terminal (typing variant only) */}
      <g className="es-holo">
        <rect x="0" y="13" width="5" height="7" fill={C.pantsShade} opacity="0.9" />
        <rect x="0" y="13" width="5" height="1" fill={C.holoDim} />
        <g className="es-holo-lines">
          <rect x="1" y="15" width="3" height="1" fill={C.holo} />
          <rect x="1" y="17" width="2" height="1" fill={C.holo} opacity="0.7" />
          <rect x="1" y="18" width="3" height="1" fill={C.holo} opacity="0.45" />
        </g>
      </g>
    </svg>
  );
}
